/**
 * Alpine.js Status Editor Component
 * Provides inline editing functionality for user status field
 */

import { editorBase } from './alpine-editor-base.js';
import { SM_BREAKPOINT } from './breakpoints.js';

/**
 * Status Editor Component Factory
 * Handles user status updates
 */
export function statusEditor() {
    return {
        // State
        isEditing: false,
        value: '',
        originalValue: '',
        isSaving: false,
        error: null,
        
        // Configuration (set from data attributes or defaults)
        fieldName: 'status',
        endpoint: '/v1/user-status/self',
        maxLength: 140, // Twitter-like limit for status
        placeholder: 'What\'s on your mind?',
        
        // Computed properties
        get hasChanges() {
            return this.value !== this.originalValue;
        },
        
        get charCount() {
            return this.value.length;
        },
        
        get displayValue() {
            if (!this.value) {
                return '<span class="text-gray-400 italic">Add your status</span>';
            }
            return editorBase.escapeHtml(this.value);
        },
        
        // Methods
        getInput() {
            if (window.innerWidth < SM_BREAKPOINT && this.$refs.inputMobile) {
                return this.$refs.inputMobile;
            }
            return this.$refs.input;
        },

        init() {
            // Initialize from element data attributes
            const element = this.$el;
            
            this.fieldName = element.dataset.field || this.fieldName;
            this.endpoint = element.dataset.endpoint || this.endpoint;
            this.maxLength = parseInt(element.dataset.maxLength) || this.maxLength;
            this.placeholder = element.dataset.placeholder || this.placeholder;
            
            // Get initial value from element's data attributes
            let rawValue = element.dataset.value || '';
            
            // If no value on element, check for .inline-edit-content child (legacy)
            if (!rawValue) {
                const contentEl = element.querySelector('.inline-edit-content');
                if (contentEl) {
                    rawValue = contentEl.dataset.value || contentEl.textContent.trim() || '';
                    // Remove placeholder text if present
                    if (rawValue === 'Add your status') {
                        rawValue = '';
                    }
                }
            }
            
            // Decode HTML entities if the value came from a data attribute
            if (rawValue) {
                rawValue = editorBase.decodeHtml(rawValue);
            }
            
            this.originalValue = rawValue;
            this.value = this.originalValue;
            
            // Initialize base utilities
            editorBase.initEditor(this);
        },
        
        startEdit() {
            this.value = this.originalValue;
            this.isEditing = true;
            this.error = null;

            if (window.innerWidth < SM_BREAKPOINT) {
                window.scrollLock?.lock();
            }

            // Register with store for tracking
            if (this.$store.profile) {
                this.$store.profile.startEditing(this.fieldName);
            }

            // Focus the input after Alpine updates the DOM
            this.$nextTick(() => {
                window.LucideIcons?.initialize(this.$el);
                const input = this.getInput();
                if (input) {
                    input.focus();
                    input.setSelectionRange(input.value.length, input.value.length);
                }
            });
        },
        
        cancel() {
            this.value = this.originalValue;
            this.error = null;
            this.completeEdit();
        },
        
        completeEdit() {
            this.isEditing = false;
            window.scrollLock?.unlock();

            // Unregister from store
            if (this.$store.profile) {
                this.$store.profile.stopEditing(this.fieldName);
            }
        },
        
        updateCharCount() {
            // Character count is handled by the getter
        },
        
        handleKeydown(event) {
            if (event.key === 'Escape') {
                this.cancel();
            } else if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                this.save();
            }
        },
        
        async save() {
            if (!this.hasChanges || this.isSaving) {
                return;
            }
            
            this.isSaving = true;
            this.error = null;
            
            try {
                // Status endpoint expects 'message' field
                const result = await editorBase.saveField(this.endpoint, 'message', this.value);
                
                // Save successful - update local state
                this.originalValue = this.value;
                
                // Update store
                if (this.$store.profile) {
                    this.$store.profile.updateField(this.fieldName, this.value);
                }
                
                // Exit edit mode
                this.completeEdit();
                
            } catch (error) {
                // Show error and keep in edit mode
                this.error = error.message || 'Failed to save status. Please try again.';
                console.error('Save error:', error);
            } finally {
                this.isSaving = false;
            }
        }
    }
}